<script setup lang="ts">
import { stringToFaDateTime } from '@payjib/shared'
import { computed } from 'vue'
import {
  setDefaultPaymentSettingAction,
  updatePaymentSettingAction,
} from '@/api/functions/settings/payment-settings-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { paymentUrl } from '@/configs/urls'
import { getErrorMessage } from '@/utils/errorUtils'

defineOptions({
  name: 'PaymentSettingCard',
})

interface PaymentSetting {
  id: string
  name: string
  isActive: boolean
  isDefault: boolean
  createdAt: string
}

const props = defineProps<{
  setting: PaymentSetting
  allSettings: PaymentSetting[]
}>()

const emit = defineEmits(['updated'])

const toast = useToast()

const createdAtLabel = computed(() => stringToFaDateTime(props.setting.createdAt))

const toggleActiveMutation = useMutationApi({
  mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
    updatePaymentSettingAction(id, { activation: isActive }),

  onSuccess: () => {
    toast.success(props.setting.isActive ? 'درگاه غیرفعال شد' : 'درگاه فعال شد', 'موفق')

    emit('updated')
  },

  onError: (error: Error) => toast.error(getErrorMessage(error), 'خطا'),
})

const handleToggleActive = () => {
  toggleActiveMutation.mutate({
    id: props.setting.id,
    isActive: !props.setting.isActive,
  })
}

const setDefaultMutation = useMutationApi({
  mutationFn: (id: string) => setDefaultPaymentSettingAction(id),

  onSuccess: () => {
    toast.success('درگاه به عنوان پیش‌فرض انتخاب شد', 'موفق')

    emit('updated')
  },

  onError: (error: Error) => toast.error(getErrorMessage(error), 'خطا'),
})

const handleSetDefault = () => {
  if (props.setting.isDefault) return

  setDefaultMutation.mutate(props.setting.id)
}

// --- Gateway test -----------------------------------------------------------
// Opens the payment gateway service's test panel in a new tab. The panel itself
// fires each IPG's real "request token" flow with auto mock data. Host is
// environment-specific via VITE_PAY_GATEWAY_TEST_URL (staging vs production).

const GATEWAY_TEST_URL = import.meta.env.VITE_PAY_GATEWAY_TEST_URL || paymentUrl('/gateway-test')

const handleTestGateway = () => {
  window.open(GATEWAY_TEST_URL, '_blank', 'noopener,noreferrer')
}
</script>

<template>

  <div
    :class="setting.isDefault ? 'bg-white border-[#2d5396]' : 'bg-white border-transparent'"
    class="rounded-xl border-s-4  p-5 lg:p-8 lg:pb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-6">

    <!-- Info -->

    <div class="flex flex-col gap-2.5 text-sm text-black font-medium">

      <div class="flex items-center gap-2">

        <span class="text-black">
          {{ setting.name }}
        </span>

        <span v-if="setting.isDefault"
          class="flex items-center justify-center text-[11px] bg-[#d5ddea] text-[#2d5396] font-medium h-7 rounded-[8px] px-3">
          پیش‌فرض
        </span>

      </div>

      <div class="flex items-center gap-1 text-[13px] text-[#1b325a] font-normal">

        <span>
          وضعیت:
        </span>

        <span class="flex items-center justify-center text-[11px] font-medium h-6 rounded-md px-2.5" :class="setting.isActive
            ? 'bg-[#d5ddea] text-[#2d5396]'
            : 'bg-gray-100 text-gray-500'
          ">
          {{ setting.isActive ? 'فعال' : 'غیرفعال' }}
        </span>

      </div>

      <div class="flex items-center gap-1 text-[13px] text-[#1b325a] font-normal">

        ثبت:
        <span dir="ltr" class="ms-1 font-fa">
          {{ createdAtLabel }}
        </span>

      </div>

    </div>

    <!-- Actions -->

    <div class="w-full lg:w-fit flex flex-col items-start justify-end gap-2">


      <!-- <Button @click="handleTestGateway"
        class="w-1/2 lg:w-auto bg-transparent border-[#16a34a] border-2 hover:bg-[#16a34a]/10 rounded-[10px]! px-5 h-10! py-0 text-[13px] font-normal">

        <span class="text-[#16a34a]">
          تست درگاه
        </span>

      </Button> -->

      <Button v-if="!setting.isDefault" @click="handleSetDefault"
        class="w-full bg-transparent border-[#2d5396] border-2 hover:bg-gray-100 rounded-[10px]! px-5 h-10! py-0 text-[13px] font-normal">

        <span class="text-[#2d5396]">
          تنظیم به‌عنوان پیش‌فرض
        </span>

      </Button>
      <Button @click="handleTestGateway"
        class="w-full bg-transparent border-[#16a34a] border-2 hover:bg-[#16a34a]/10! rounded-[10px]! px-5 h-10! py-0 text-[13px] font-normal">

        <span class="text-[#16a34a]">
          تست درگاه
        </span>

      </Button>
     <!--
        Activate/deactivate buttons are temporarily disabled because the admins doesnt need them and its unnecessary.
        Re-enable them once it becomes necessary for admins to toggle the active status.

      <Button v-if="setting.isActive" @click="handleToggleActive"
        class="w-full bg-transparent border-[#2d5396] border-2 hover:bg-gray-100 rounded-[10px]! px-5 h-10! py-0 text-[13px] font-normal">

        <span class="text-[#2d5396]">
          غیرفعال کردن
        </span>

      </Button>

      <Button v-else :isAdmin="true" @click="handleToggleActive"
        class="w-full rounded-[10px]! px-5 h-10! py-0 text-[13px] font-normal">
        فعال کردن
      </Button>
      -->
    </div>

  </div>

</template>
